import React from "react";
import {
  SearchResultItemProps,
  useChatContext,
  type ChannelSearchFunctionParams,
} from "stream-chat-react";
import {
  Channel,
  ChannelFilters,
  ChannelOptions,
  MessageResponse,
  StreamChat,
} from "stream-chat";

const SEARCH_MATCH_TEXT_KEY = "__guesteriaSearchMatchText";
const SEARCH_MATCH_USER_KEY = "__guesteriaSearchMatchUser";

const isChannelResult = (result: unknown): result is Channel => {
  return Boolean(result && typeof result === "object" && "cid" in result);
};

const getMessageCid = (message: MessageResponse) => {
  return (
    message.cid ||
    (message as MessageResponse & { channel?: { cid?: string } }).channel?.cid
  );
};

const setSearchMatch = (channel: Channel, message: MessageResponse) => {
  const data = (channel.data ?? {}) as Record<string, unknown>;

  data[SEARCH_MATCH_TEXT_KEY] = message.text ?? "";
  data[SEARCH_MATCH_USER_KEY] = message.user?.name ?? message.user?.id ?? "";
};

const getSearchMatchText = (channel: Channel) => {
  const data = (channel.data ?? {}) as Record<string, unknown>;
  return typeof data[SEARCH_MATCH_TEXT_KEY] === "string"
    ? data[SEARCH_MATCH_TEXT_KEY]
    : "";
};

const getSearchMatchUser = (channel: Channel) => {
  const data = (channel.data ?? {}) as Record<string, unknown>;
  return typeof data[SEARCH_MATCH_USER_KEY] === "string"
    ? data[SEARCH_MATCH_USER_KEY]
    : "";
};

const getChannelTitle = (channel: Channel, currentUserId?: string) => {
  const isGroup = channel.data?.chat_type === "group";

  if (isGroup && typeof channel.data?.name === "string") {
    return channel.data.name;
  }

  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== currentUserId,
  )?.user;

  return (
    (typeof channel.data?.name === "string" ? channel.data.name : undefined) ||
    otherMember?.name ||
    channel.data?.created_by?.name ||
    channel.id ||
    channel.cid
  );
};

export const createMessageSearchFunction = ({
  client,
  filters,
  options,
}: {
  client: StreamChat;
  filters: ChannelFilters;
  options?: ChannelOptions;
}) => {
  return async (
    { setQuery, setResults, setSearching }: ChannelSearchFunctionParams,
    event: React.BaseSyntheticEvent,
  ) => {
    const query = String(event.target?.value ?? "").trim();
    setQuery(query);

    if (!query) {
      setResults([]);
      setSearching(false);
      return;
    }

    try {
      setSearching(true);

      const searchResponse = await client.search(filters, query, { limit: 10 });
      const messagesByCid = new Map<string, MessageResponse>();

      for (const result of searchResponse.results) {
        const cid = getMessageCid(result.message);

        if (cid && !messagesByCid.has(cid)) {
          messagesByCid.set(cid, result.message);
        }
      }

      const cids = [...messagesByCid.keys()];

      if (!cids.length) {
        setResults([]);
        return;
      }

      const channels = await client.queryChannels(
        {
          cid: { $in: cids },
          members: filters.members,
          type: filters.type,
        },
        {},
        { limit: cids.length, ...options },
      );
      const channelsByCid = new Map(
        channels.map((channel) => [channel.cid, channel]),
      );
      const orderedChannels = cids
        .map((cid) => {
          const channel = channelsByCid.get(cid);
          const message = messagesByCid.get(cid);

          if (channel && message) {
            setSearchMatch(channel, message);
          }

          return channel;
        })
        .filter((channel): channel is Channel => Boolean(channel));

      setResults(orderedChannels);
    } catch (error) {
      console.error("Failed to search chat messages:", error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };
};

export const MessageSearchResultItem = ({
  result,
  selectResult,
}: SearchResultItemProps) => {
  const { client } = useChatContext();

  if (!isChannelResult(result)) {
    return null;
  }

  const title = getChannelTitle(result, client.userID);
  const matchText = getSearchMatchText(result);
  const matchUser = getSearchMatchUser(result);

  return (
    <button
      className="str-chat__channel-search-result flex w-full flex-col items-start gap-1 px-4 py-3 text-left"
      onClick={() => selectResult(result)}
      role="option"
      type="button"
    >
      <span className="text-sm font-semibold text-slate-900">{title}</span>
      {matchText ? (
        <span className="line-clamp-2 text-xs text-slate-600">
          {matchUser ? `${matchUser}: ` : ""}
          {matchText}
        </span>
      ) : null}
    </button>
  );
};
